import axios, { type AxiosError, type AxiosRequestConfig } from "axios";
import { resetAuthPersist, useAuthStore } from "../store/auth";

declare module "axios" {
  export interface AxiosRequestConfig {
    _retry?: boolean;
    _csrfRetried?: boolean;
  }
}

const LAST_ORG_SLUG_KEY = "kwad.storefront.last-org-slug";

export function getRememberedOrgSlug(): string | null {
  try {
    const stored = window.localStorage.getItem(LAST_ORG_SLUG_KEY);
    return stored?.trim() || null;
  } catch {
    return null;
  }
}

export function rememberOrgSlug(slug: string | null | undefined) {
  if (!slug) return;
  try {
    window.localStorage.setItem(LAST_ORG_SLUG_KEY, slug);
  } catch {
    // no-op
  }
}

export function resolveStorefrontOrgSlug(): string | null {
  const fromStore = useAuthStore.getState().organization?.slug?.trim();
  if (fromStore) {
    rememberOrgSlug(fromStore);
    return fromStore;
  }

  if (typeof window !== "undefined") {
    // 1. Subdomain-based: minegocio-default.getkwad.com → "minegocio-default"
    const hostname = window.location.hostname;
    const ROOT_DOMAIN = "getkwad.com";
    if (hostname.endsWith(`.${ROOT_DOMAIN}`) && hostname !== ROOT_DOMAIN) {
      const subdomain = hostname.slice(0, -(ROOT_DOMAIN.length + 1));
      // Excluir subdomains de servicios (storefront, app, admin, api)
      const RESERVED_SUBDOMAINS = new Set([
        "storefront",
        "app",
        "admin",
        "api",
        "www",
        "mail",
      ]);
      if (!RESERVED_SUBDOMAINS.has(subdomain) && subdomain.length > 0) {
        const slug = subdomain.toLowerCase();
        rememberOrgSlug(slug);
        return slug;
      }
    }

    // 2. Path-based: /s/:slug/...
    const pathname = window.location.pathname;
    const match = pathname.match(/^\/s\/([^/]+)/i);
    if (match?.[1]) {
      const slug = decodeURIComponent(match[1]).trim().toLowerCase();
      rememberOrgSlug(slug);
      return slug;
    }
  }

  const fromEnv = String(import.meta.env.VITE_STOREFRONT_ORG_SLUG ?? "")
    .trim()
    .toLowerCase();
  if (fromEnv) {
    rememberOrgSlug(fromEnv);
    return fromEnv;
  }

  return getRememberedOrgSlug();
}

export function buildStorefrontApiPath(path: string, orgSlug?: string | null) {
  const slug = (orgSlug ?? resolveStorefrontOrgSlug())?.trim().toLowerCase();

  if (!slug) {
    throw new Error(
      "No se pudo resolver el orgSlug del storefront. Abre la tienda desde /s/:orgSlug o define VITE_STOREFRONT_ORG_SLUG.",
    );
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `/api/public/storefront/${slug}${normalizedPath}`;
}

export const api = axios.create({
  baseURL: "",
  withCredentials: true,
  headers: {
    Accept: "application/json",
  },
});

export const apiBaseURL = "/api";

// Lane 2.5.A — helpers CSRF (double-submit pattern). El secret vive en una
// cookie httpOnly (`kwad_storefront_csrf`) y el par público en el store.
// Solo se envía `x-csrf-token` en métodos mutativos bajo /api/public/storefront/.
const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
const CSRF_EXEMPT_SUFFIXES = [
  "/auth/register",
  "/auth/login",
  "/auth/refresh",
  "/auth/logout",
  // Sprint 5 Slice 2 — flows de email accesibles por link sin sesión previa.
  "/auth/verify-email",
  "/auth/resend-verification",
  "/auth/forgot-password",
  "/auth/reset-password",
];

function isStorefrontMutation(
  method: string | undefined,
  url: string | undefined,
): boolean {
  if (!method || !url) return false;
  if (!MUTATING_METHODS.has(method.toUpperCase())) return false;
  const path = extractPath(url);
  if (!/^\/api\/public\/storefront\/[^/]+\//i.test(path)) return false;
  return !CSRF_EXEMPT_SUFFIXES.some((suffix) => path.endsWith(suffix));
}

function isCsrfRecoveryRequest(url: string | undefined): boolean {
  const path = extractPath(url);
  return /\/api\/public\/storefront\/[^/]+\/auth\/csrf$/i.test(path);
}

function isCsrfErrorBody(data: unknown): boolean {
  if (!data || typeof data !== "object") return false;
  const candidate = data as { code?: unknown; error?: { code?: unknown } };
  const code =
    typeof candidate.code === "string"
      ? candidate.code
      : typeof candidate.error?.code === "string"
        ? candidate.error.code
        : null;
  return typeof code === "string" && code.startsWith("FST_CSRF");
}

api.interceptors.request.use((config) => {
  const state = useAuthStore.getState();
  const token = state.accessToken;

  if (token) {
    config.headers = config.headers ?? {};
    if (!("Authorization" in config.headers)) {
      (config.headers as Record<string, string>).Authorization =
        `Bearer ${token}`;
    }
  }

  if (isStorefrontMutation(config.method, config.url) && state.csrfToken) {
    config.headers = config.headers ?? {};
    if (!("x-csrf-token" in config.headers)) {
      (config.headers as Record<string, string>)["x-csrf-token"] =
        state.csrfToken;
    }
  }

  return config;
});

let isRefreshing = false;
let waiters: Array<(token: string | null) => void> = [];

function extractPath(url?: string): string {
  if (!url) return "";
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname;
  } catch {
    return url.replace(/^https?:\/\/[^/]+/i, "");
  }
}

function isStorefrontAuthRequest(url?: string): boolean {
  const path = extractPath(url);
  return /\/api\/public\/storefront\/[^/]+\/auth\//i.test(path);
}

async function refreshAccessToken(): Promise<string | null> {
  try {
    const res = await api.post(buildStorefrontApiPath("/auth/refresh"));
    const payload = res.data?.data ?? res.data ?? null;
    const newToken = (payload?.accessToken ?? null) as string | null;

    if (!newToken) {
      useAuthStore.getState().logoutLocal();
      resetAuthPersist();
      return null;
    }

    // TODO: ADR 0005 — slug mismatch handling
    // Compare `payload.organization.slug` against the URL's `/s/:orgSlug`
    // (not `resolveStorefrontOrgSlug()`, which already prefers the persisted
    // store) and, on mismatch, call `logout()` + `resetAuthPersist()`, set
    // the `kwad_suppress_refresh_attempt` sessionStorage flag, and redirect
    // to `/s/:urlSlug/login?reason=subject_mismatch`. Deferred: the cookie
    // `kwad_storefront_refresh` is already path-scoped per slug, so the
    // cross-path bleed exploited in /api/auth does not apply here; the
    // remaining risk is localStorage-persisted `kwad.storefront.auth`
    // hydrating stale identity across orgs, which needs both a URL-only
    // resolver and a mount-time check.
    useAuthStore.getState().setSession({
      accessToken: newToken,
      csrfToken: (payload.csrfToken ?? null) as string | null,
      customer: payload.customer,
      organization: payload.organization,
    });

    return newToken;
  } catch {
    useAuthStore.getState().logoutLocal();
    resetAuthPersist();
    return null;
  }
}

// Lane 2.5.A — refresca el CSRF token contra GET /auth/csrf. Requiere sesión
// válida (si no hay, el endpoint devuelve 401). Devuelve el nuevo token o
// null si no se pudo recuperar.
async function refreshCsrfToken(): Promise<string | null> {
  try {
    const res = await api.get(buildStorefrontApiPath("/auth/csrf"));
    const payload = res.data?.data ?? res.data ?? null;
    const newToken = (payload?.csrfToken ?? null) as string | null;
    if (newToken) {
      useAuthStore.getState().setCsrfToken(newToken);
    }
    return newToken;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const status = err.response?.status;
    const original = err.config as
      | (AxiosRequestConfig & { _retry?: boolean; _csrfRetried?: boolean })
      | undefined;

    if (!original) {
      return Promise.reject(err);
    }

    // Lane 2.5.A — recuperación CSRF: 403 con body code FST_CSRF_* en ruta
    // mutativa del storefront → GET /auth/csrf + retry una sola vez.
    // No se aplica al propio endpoint de recuperación (previene loops).
    if (
      status === 403 &&
      !original._csrfRetried &&
      !isCsrfRecoveryRequest(original.url) &&
      isStorefrontMutation(original.method, original.url) &&
      isCsrfErrorBody(err.response?.data)
    ) {
      original._csrfRetried = true;
      const newCsrf = await refreshCsrfToken();
      if (!newCsrf) {
        return Promise.reject(err);
      }
      original.headers = original.headers ?? {};
      (original.headers as Record<string, string>)["x-csrf-token"] = newCsrf;
      return api(original);
    }

    if (status !== 401 || isStorefrontAuthRequest(original.url)) {
      return Promise.reject(err);
    }

    if (original._retry) {
      return Promise.reject(err);
    }
    original._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        waiters.push((newToken) => {
          if (newToken) {
            original.headers = original.headers ?? {};
            (original.headers as Record<string, string>).Authorization =
              `Bearer ${newToken}`;
            resolve(api(original));
          } else {
            reject(err);
          }
        });
      });
    }

    try {
      isRefreshing = true;
      const newToken = await refreshAccessToken();

      waiters.forEach((fn) => fn(newToken));
      waiters = [];

      if (!newToken) {
        return Promise.reject(err);
      }

      original.headers = original.headers ?? {};
      (original.headers as Record<string, string>).Authorization =
        `Bearer ${newToken}`;

      return api(original);
    } finally {
      isRefreshing = false;
    }
  },
);
